import { compact } from 'lodash/fp';
import { Interval } from 'luxon';
import { FC, useMemo } from 'react';

import { workflowStepIcon } from 'dashboard/apps/InquiryTemplateEditor/WorkflowTree';
import { WorkflowRunStepStatsCard } from 'dashboard/components-lib/WorkflowRunStepStats';
import { hasBranch, hasBranches } from 'dashboard/lib/workflows';
import { humanReadableRunTime, startEndTime } from 'dashboard/lib/workflows/run-stats';
import { WorkflowRunStep, WorkflowRunStepLoop } from 'dashboard/models/workflow-step';
import { WorkflowStep, WorkflowStepType } from 'dashboard/models/workflow-version';
import { TimelineItem } from 'dashboard/views/DashboardWorkflowRunsShowView/TimelineItem';
import { intervalPercentile } from 'dashboard/views/DashboardWorkflowRunsShowView/utils';
import WorkflowRunBlockTimeline from 'dashboard/views/DashboardWorkflowRunsShowView/WorkflowRunBlockTimeline';
import { CommonTimelineProps } from 'dashboard/views/DashboardWorkflowRunsShowView/WorkflowRunTimeline';
import useToggle from 'lib/hooks/useToggle';
import { ApiModel } from 'lib/interfaces/api';

interface WorkflowRunStepTimelineProps {
  step: WorkflowRunStep;
  indent?: number;
  visible?: boolean;
  setIsResizingColumns: (isResizing: boolean) => void;
  totalInterval: Interval;
  flexibleColumnWidth: number;
}

const WorkflowRunStepTimeline: FC<WorkflowRunStepTimelineProps & CommonTimelineProps> = ({
  tokensToSteps,
  tokensToRunSteps,
  step,
  allRunSteps,
  indent = 0,
  visible = true,
  totalInterval,
  searchString,
  flexibleColumnWidth,
  viewMode,
  activeNode,
  selectNode,
  setIsResizingColumns,
  updateLoopCycle,
}) => {
  const configStep = useMemo<WorkflowStep | null>(() => {
    return tokensToSteps[step.attributes['config-step-token']]?.step;
  }, [step, tokensToSteps]);

  const childrenBranches = useMemo<WorkflowRunStep[][]>(() => {
    if (!configStep) {
      return [];
    }

    if (hasBranches(configStep)) {
      return configStep.branches
        .map((branch) => compact(branch.map((configStep) => tokensToRunSteps[configStep.token])))
        .filter((n) => n.length > 0);
    }

    if (hasBranch(configStep)) {
      const loopRunStep = step as WorkflowRunStepLoop;
      const childrenConfigSteps = configStep.branch.map((configStep) => configStep.token);
      return Object.entries(loopRunStep['attributes']['cycle-to-step-tokens']).map((entry) =>
        compact(
          entry[1].map((stepToken) =>
            allRunSteps.find(
              (step) =>
                step.id === stepToken &&
                childrenConfigSteps.includes(step.attributes['config-step-token']),
            ),
          ),
        ),
      );
    }
    return [];
  }, [allRunSteps, configStep, step, tokensToRunSteps]);

  const scopeTokensToRunSteps = useMemo((): Record<number, Record<string, WorkflowRunStep>> => {
    return childrenBranches.reduce((obj, _, index) => {
      // No need to scope conditional and parallel children since there is no overlap in config tokens
      if (!configStep || configStep.type !== WorkflowStepType.Loop) {
        return {
          ...obj,
          [index]: tokensToRunSteps,
        };
      }

      const loopRunStep = step as WorkflowRunStepLoop;
      const scopedTokensToRunSteps = loopRunStep['attributes']['cycle-to-step-tokens'][
        index
      ].reduce((scoped, stepToken) => {
        const runStep = allRunSteps.find((s) => s.id === stepToken);
        if (!runStep) {
          return scoped;
        }

        return {
          ...scoped,
          [runStep['attributes']['config-step-token']]: runStep,
        };
      }, {});

      return {
        ...obj,
        [index]: scopedTokensToRunSteps,
      };
    }, {});
  }, [allRunSteps, childrenBranches, configStep, step, tokensToRunSteps]);

  const loopAncestorCycle = useMemo(() => {
    const loopSteps = allRunSteps.filter((step) => step.type === ApiModel.WorkflowRunStepLoop);

    return loopSteps.reduce((attr, s) => {
      const loopStep = s as WorkflowRunStepLoop;
      if (loopStep.attributes['cycle-to-step-tokens']) {
        const cycle = Object.entries(loopStep['attributes']['cycle-to-step-tokens'])
          .filter(([_, tokens]) => tokens.includes(step.id))
          .map(([cycle]) => cycle);

        if (cycle.length > 0) {
          return { ...attr, [loopStep.attributes['config-step-token']]: cycle[0] };
        }
      }
      return attr;
    }, {});
  }, [allRunSteps, step.id]);

  const [expanded, { toggle: toggleChildren }] = useToggle(false);

  const { durationString, startFraction, spanFraction } = useMemo(() => {
    const { startTime, endTime } = startEndTime([step]);

    const { durationString } = humanReadableRunTime(startTime, endTime);

    const { startFraction, spanFraction } = intervalPercentile(
      Interval.fromDateTimes(startTime, endTime),
      totalInterval,
    );

    return { durationString, startFraction, spanFraction };
  }, [step, totalInterval]);

  const icon = useMemo(() => {
    if (!configStep) {
      return null;
    }
    return workflowStepIcon(configStep.type, 18);
  }, [configStep]);

  return (
    <>
      {configStep && (
        <>
          <TimelineItem
            id={step.id}
            name={configStep.name}
            icon={icon}
            duration={durationString}
            indent={searchString.length === 0 ? indent : 0}
            childrenVisible={expanded}
            visible={
              searchString.length === 0
                ? visible
                : configStep.name.toLowerCase().includes(searchString.toLowerCase())
            }
            disableToggle={searchString.length > 0}
            startFraction={startFraction}
            lengthFraction={spanFraction}
            status={step.attributes['status']}
            flexibleColumnWidth={flexibleColumnWidth}
            viewMode={viewMode}
            tooltip={<WorkflowRunStepStatsCard runStep={step} stepName={configStep.name} />}
            onSelect={() => {
              updateLoopCycle(loopAncestorCycle);
              selectNode(configStep.token || null);
            }}
            toggleChildren={childrenBranches.length > 0 ? toggleChildren : null}
            setIsResizingColumns={setIsResizingColumns}
          />
          {childrenBranches.map((branch, index) => {
            return (
              <WorkflowRunBlockTimeline
                key={`workflow-run-diagnostics-step__${step.id}__branch-${index}`}
                id={`${step.id}__branch-${index}`}
                indent={indent + 1}
                index={index}
                type={configStep.type}
                visible={visible && expanded}
                steps={branch}
                tokensToSteps={tokensToSteps}
                tokensToRunSteps={scopeTokensToRunSteps[index]}
                allRunSteps={allRunSteps}
                totalInterval={totalInterval}
                name={configStep.name}
                searchString={searchString}
                flexibleColumnWidth={flexibleColumnWidth}
                viewMode={viewMode}
                activeNode={activeNode}
                selectNode={selectNode}
                setIsResizingColumns={setIsResizingColumns}
                updateLoopCycle={updateLoopCycle}
              />
            );
          })}
        </>
      )}
    </>
  );
};

export default WorkflowRunStepTimeline;
